// My Drive: the rail, the top bar and the listing for the place the route names — the top of the
// drive, one folder in it, Recent, Starred or Trash.
//
// Folders sit inside one another. The API hands over every folder at once, each naming its parent
// and saying what it holds, so a place lists the folders whose parent it is, and the trail of
// folders above it is found by walking up those parents. The top of the drive lists the folders
// that are in none of the listed folders: those with no parent, and those in a folder the person
// can't see, which is the system's own root folder. New folders made at the top and uploads made
// without a folder land in it. That folder is kept out of the list.
//
// Files are another matter: a folder can hold thousands. The API hands over the files in the place
// on screen, newest first, a hundred at a time, and the next hundred are asked for as the listing
// is scrolled near the end of those so far (see "usePagedUploads"), until there are no more. A
// search is the API's to answer too, since it has to cover the files not scrolled to yet; while it
// is on its way, what is already listed is narrowed to it.
//
// Everything that puts something here lives on this screen: New in the rail, New folder in the
// toolbar, and files dropped anywhere over the listing. All of it goes into the folder on screen, or
// the top of the drive outside one. A folder chosen from disk keeps its shape, every folder in it
// made inside the folder on screen.
//
// So does everything that takes something back out. A secured file opens over the drive in the
// preview, stepping through the secured files listed beside it, and downloads from its row or the
// preview's header. A download takes a few seconds to prepare, so a notice says it is on its way,
// and another says so if it fails.
//
// Recent lists the 30 files last previewed or downloaded, the latest first, and Starred the files
// starred from their menus, the latest starred first, a page at a time as a folder's are. The API
// only ever lists what is still in the drive in either. Nothing is put into either, so neither
// offers a new folder or takes drops.
//
// Deleting sends an item to Trash, after asking. Trash is this same screen listing what is in it,
// where an item can be restored, or deleted for good after asking again. Nothing is dropped into
// Trash, so its listing takes no drops.
//
// A folder can't go to Trash while an upload is still under way in it, or in any folder below it:
// one the API is still securing, or one in the tray still on its way. The API makes the folders a
// file from disk is put in as it hands out the file's url, so a folder sent to Trash in that moment
// could leave one it was making behind, outside Trash, for the purge to take with it later. Each
// folder says whether the API is still securing a file in it or below it. The folders a transfer is
// going into are judged by those the drive has so far.
//
// Anything outside Trash can be renamed from its menu, where it is, and a name already taken beside
// it is refused there.
//
// Anything in the drive, Recent or Starred can be moved into another folder, or to the top of the
// drive: from its menu or the selection bar, which open a dialog to pick the folder, or in the drive
// itself by dragging it onto a folder in the listing or one of the folders in the trail above.
// Dragging something selected moves the whole selection. A folder takes everything in it along. An
// item whose name is already taken where it would go stays where it is, and the notice says so.
//
// Items can be selected to act on several at once: in the drive to download or delete them, in
// Trash to restore them or delete them for good. A selection belongs to the place it was made in —
// the top of the drive, one folder, Recent, Starred or Trash — and only what is listed there now
// counts towards it, so moving elsewhere or narrowing the search never acts on anything out of
// sight. Downloading a selection saves the secured files in it; folders, and files still being
// secured, have nothing to save yet. What leaves the place it was selected in, by any of these,
// leaves the selection.
//
// Locked is a place of its own, with folders inside it like the drive's, that only this tab can open
// and only with its passphrase. Sealed, it lists what it holds as redaction bars with their sizes,
// and offers to unlock; open, it lists like the drive does, counts down to when it reseals, and
// takes uploads, new folders and moves within it. Anything in the drive can be moved into it, and
// anything in it back out to the top of the drive, after asking — which first asks for Locked to be
// unlocked if it is sealed, or for a passphrase if none has been set. While it is open, what was
// deleted from it is in Trash too. Resealing, by its countdown, Lock now or the API's say-so,
// closes whatever of it is on screen.
//
// Those it is offered to can also import from Google Drive or OneDrive, from New in the rail: a
// dialog first says what to expect, and its Continue opens the provider's picker to choose the files, and the API copies them into the folder on screen, or the top
// of the drive, never Locked. The first OneDrive import only signs in, since a click lets through one
// popup and the picker is a second, so a notice then offers to choose the files with one more click. While the API looks over a pick,
// which takes a while for a large one, a notice says so. The import's files then follow in the upload tray, and once it ends a dialog says what
// came in and what didn't. A file that didn't stays where it is, marked failed, until it's deleted.
// An import the API won't start is told in a notice, in its own words.
//
// Hidden, Locked leaves the rail while sealed, and its passphrase typed into the search box is the
// way in: once typing pauses on something long enough to be one, it is tried, and the field empties
// the moment it opens Locked. Anything short of the passphrase is an ordinary search.
import classnames from 'classnames'
import {useEffect, useRef, useState} from 'react'
import {Navigate, useNavigate, useParams} from 'react-router-dom'
import {useApolloClient, useMutation, useQuery} from '@apollo/client/react'
import PropTypes from 'prop-types'

import Snackbar from '@mui/material/Snackbar'

import DriveItems, {selectionKey} from './DriveItems'
import DriveRail from './DriveRail'
import DriveToolbar from './DriveToolbar'
import DriveTopbar from './DriveTopbar'
import DropZone from './DropZone'
import FilePreview from './FilePreview'
import ImportResultsDialog from './ImportResultsDialog'
import ImportStartDialog from './ImportStartDialog'
import Loader from '../shared/Loader'
import LockedMoveDialog from './LockedMoveDialog'
import LockedSeal from './LockedSeal'
import LockedSettingsDialog from './LockedSettingsDialog'
import MoveDialog from './MoveDialog'
import NewFolderDialog from './NewFolderDialog'
import PassphraseDialog, {SHORTEST_PASSPHRASE} from './PassphraseDialog'
import RenameDialog from './RenameDialog'
import ResetLockedDialog from './ResetLockedDialog'
import TrashDialog, {itemsInput} from './TrashDialog'
import UnlockDialog from './UnlockDialog'
import UploadTray from './UploadTray'
import XPostDialog from './XPostDialog'
import {BaseButton} from '../shared/blocks/Buttons'

import {CURRENT_USER} from '../../graphql/queries/user'
import {STORAGE} from '../../graphql/queries/storage'
import {FOLDERS} from '../../graphql/queries/folders'
import {LOCKED} from '../../graphql/queries/locked'
import {LOCK_LOCKED, UNLOCK_LOCKED} from '../../graphql/mutations/locked'
import {MOVE_ITEMS} from '../../graphql/mutations/moves'
import {UPDATE_PREFERENCES} from '../../graphql/mutations/preferences'
import {RESTORE_ITEMS} from '../../graphql/mutations/trash'
import {STAR_ITEMS, UNSTAR_ITEMS} from '../../graphql/mutations/stars'
import {TRASH} from '../../graphql/queries/trash'
import {RECENT_UPLOADS, STARRED_UPLOADS, UPLOADS, UPLOAD_LISTINGS} from '../../graphql/queries/uploads'
import {ROOT_FOLDER, isResealing, isSecured} from '../../lib/fileTools'
import {endSession} from '../../lib/sessionTools'
import {openLocked, sealLocked} from '../../lib/lockedTools'
import {folderTallies, isChildOf, placeOf, trailTo} from '../../lib/folderTools'
import {pickFromGoogle, prepareGooglePicker} from '../../lib/googlePicker'
import {pickFromOneDrive, prepareOneDrivePicker} from '../../lib/oneDrivePicker'
import useDownloads from '../../lib/hooks/useDownloads'
import useImports, {refusalOf} from '../../lib/hooks/useImports'
import useLockedPictures from '../../lib/hooks/useLockedPictures'
import useLocked from '../../lib/hooks/useLocked'
import usePagedUploads from '../../lib/hooks/usePagedUploads'
import useUploads, {SETTLED_PHASES} from '../../lib/hooks/useUploads'

import './myDrive.scss'

export const SECTIONS = ['drive', 'recent', 'starred', 'trash', 'locked']

// The folders a file chosen from disk sits in, its own name taken off.
const folderPath = file => file.webkitRelativePath.split('/').slice(0, -1).join('/')

// The deepest of the folders named below "parentId" that the drive has so far.
const deepestOnPath = (parentId, names, folders, byId) => {
  const next = names.length && folders.find(entry => entry.name === names[0] && isChildOf(entry, parentId, byId))

  return next ? deepestOnPath(next.id, names.slice(1), folders, byId) : parentId
}

const transferFolderId = ({folderId, path}, folders, byId) => deepestOnPath(folderId, (path || '').split('/').filter(Boolean), folders, byId)

const busyFolderIds = (folders, byId, tallies, transfers) => {
  const holding = [
    ...folders.filter(({id}) => tallies.get(id)?.busy).map(({id}) => id),
    ...transfers.filter(({phase}) => !SETTLED_PHASES.includes(phase)).map(transfer => transferFolderId(transfer, folders, byId))
  ]

  return new Set(holding.flatMap(id => trailTo(byId.get(id), byId).map(entry => entry.id)))
}

const NOTICE_DURATION = 4000

const IMPORT_PICKERS = {
  google: {name: 'Google Drive', pick: pickFromGoogle},
  onedrive: {name: 'OneDrive', pick: pickFromOneDrive}
}

const plural = (count, noun = 'item') => `${count} ${noun}${count === 1 ? '' : 's'}`

const countOf = ({folderIds, uploadIds}) => folderIds.length + uploadIds.length

const keysOf = ({folderIds, uploadIds}) => [
  ...folderIds.map(id => selectionKey({kind: 'folder', id})),
  ...uploadIds.map(id => selectionKey({kind: 'file', id}))
]

// One item is named; several are counted.
const itemsLabel = entries => (entries.length === 1 ? `“${entries[0].name}”` : plural(entries.length))

const uploadingNotice = folders => (folders.length === 1 ?
  `“${folders[0].name}” can’t be moved to Trash until its uploads finish.` :
  `${plural(folders.length, 'folder')} can’t be moved to Trash until their uploads finish.`)

// Restoring skips anything whose place has since been taken by something of the same name.
const restoredNotice = (entries, restored) => {
  if (restored === entries.length) return `${itemsLabel(entries)} restored`
  if (restored) return `${restored} of ${plural(entries.length)} restored. Something with the others’ names is already in their place.`
  if (entries.length === 1) return `${itemsLabel(entries)} couldn’t be restored. Something with its name is already in its place.`

  return `${itemsLabel(entries)} couldn’t be restored. Something with their names is already in their place.`
}

const TOP_NAME = 'My Drive'

const LOCKED_NAME = 'Locked'

// Moving, like restoring, skips anything whose name is already taken where it would go.
const movedNotice = (entries, moved, destination, top = TOP_NAME) => {
  const where = destination?.name ?? top

  if (moved === entries.length) return `${itemsLabel(entries)} moved to ${where}`
  if (moved) return `${moved} of ${plural(entries.length)} moved to ${where}. Something with the others’ names is already there.`
  if (entries.length === 1) return `${itemsLabel(entries)} couldn’t be moved. Something with its name is already in ${where}.`

  return `${itemsLabel(entries)} couldn’t be moved. Something with their names is already in ${where}.`
}

// Crossing into Locked or out of it also skips a folder with an upload still under way inside.
const crossedNotice = (entries, moved, direction) => {
  const where = direction === 'in' ? `moved to ${LOCKED_NAME}` : `moved out of ${LOCKED_NAME}`

  if (moved === entries.length) return `${itemsLabel(entries)} ${where}`
  if (moved) return `${moved} of ${plural(entries.length)} ${where}. The others’ names are taken there, or they’re still uploading.`

  return `${itemsLabel(entries)} couldn’t be ${where}. ${entries.length === 1 ? 'Its name is' : 'Their names are'} taken there, or still uploading.`
}

// Typing in the search box is let settle this long before it is tried as Locked's passphrase.
const SEARCH_PAUSE = 600

// And this long before the API is asked for the files that match it.
const SEARCH_SETTLE = 300

// The move dialog opens where the items are, when they are all in one place.
const startOf = sources => (sources.every(source => source === sources[0]) ? sources[0] : null)

const DONE_NOTICES = {
  trash: ({trashItems}) => `${plural(countOf(trashItems))} moved to Trash`,
  purge: ({purgeItems}) => `${plural(countOf(purgeItems))} deleted for good`
}

const MyDrive = ({section = 'drive'}) => {
  const inTrash = section === 'trash'
  const inLocked = section === 'locked'
  const {folderId} = useParams()
  const client = useApolloClient()
  const navigate = useNavigate()
  const lockedSession = useLocked()
  const {data: userData} = useQuery(CURRENT_USER)
  const {data: storageData} = useQuery(STORAGE)
  const {data: folderData, loading: loadingFolders} = useQuery(FOLDERS)
  // Settled from what is typed into the search box, for the API to search.
  const [searched, setSearched] = useState('')
  const search = searched || null
  // A folder named in the route that the drive doesn't have is left, below, rather than asked after.
  const unknownFolder = Boolean(folderId && folderData && !folderData.folders.some(({id}) => id === folderId))
  const driveListing = usePagedUploads(UPLOADS, {
    field: 'uploads',
    variables: {folderId: folderId ?? null, search},
    skip: section !== 'drive' || unknownFolder
  })
  // Starred, like Recent, asks again each time it is visited, for what was starred elsewhere since.
  const starredListing = usePagedUploads(STARRED_UPLOADS, {
    field: 'starredUploads',
    variables: {search},
    skip: section !== 'starred',
    fetchPolicy: 'cache-and-network'
  })
  // Opening a file is only recorded by the API, so Recent asks again each time it is visited. It
  // is not kept current while it is on screen: a file opened from it would jump to the top under
  // the preview, and stepping to the next file would skip around.
  const {data: recentData, loading: loadingRecent} = useQuery(RECENT_UPLOADS, {
    skip: section !== 'recent',
    fetchPolicy: 'cache-and-network'
  })
  const {data: trashData, loading: loadingTrash} = useQuery(TRASH, {skip: !inTrash})
  // Asked for on Locked's own screen, and in Trash while Locked is open, for what was deleted from it.
  const {data: lockedData, loading: loadingLocked} = useQuery(LOCKED, {skip: !inLocked && !(inTrash && lockedSession.open)})
  const [restoreItems] = useMutation(RESTORE_ITEMS, {refetchQueries: [FOLDERS, ...UPLOAD_LISTINGS, TRASH, LOCKED]})
  const [starItems] = useMutation(STAR_ITEMS, {refetchQueries: UPLOAD_LISTINGS})
  const [unstarItems] = useMutation(UNSTAR_ITEMS, {refetchQueries: UPLOAD_LISTINGS})
  const [moveItems] = useMutation(MOVE_ITEMS, {refetchQueries: [FOLDERS, ...UPLOAD_LISTINGS]})
  const [updatePreferences] = useMutation(UPDATE_PREFERENCES)
  const [lockLocked] = useMutation(LOCK_LOCKED)
  const [tryPassphrase] = useMutation(UNLOCK_LOCKED)
  const {upload, uploads: transfers, dismissUploads} = useUploads()
  const {save, ...downloads} = useDownloads()
  const importEnabled = Boolean(storageData?.storage.importEnabled)
  const imports = useImports({enabled: importEnabled})

  // Chosen here until the saved preference comes back, so the view changes without waiting on it.
  const [chosenView, setChosenView] = useState(null)
  const [query, setQuery] = useState('')
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [previewId, setPreviewId] = useState(null)
  const [removing, setRemoving] = useState(null)
  const [moving, setMoving] = useState(null)
  const [renaming, setRenaming] = useState(null)
  const [crossing, setCrossing] = useState(null)
  // The provider whose import is being introduced, before its picker opens.
  const [startingImport, setStartingImport] = useState(null)
  const [importingPost, setImportingPost] = useState(false)
  // A Locked dialog: "unlock", "passphrase", "settings" or "reset", with what to carry on with once
  // Locked is open, for one on the way to something else.
  const [asking, setAsking] = useState(null)
  // What is being dragged, kept here since a drag's contents can only be read once it is dropped.
  const [dragging, setDragging] = useState(null)
  const [selection, setSelection] = useState({place: null, keys: []})
  // "lasting" keeps a notice up until something replaces it, for one that is waiting on an outcome.
  // "action" is a button on it, as a label and what it does.
  const [notice, setNotice] = useState(null)
  const filePicker = useRef(null)
  const folderPicker = useRef(null)
  const searchAttempt = useRef(null)
  const searchSettle = useRef(null)
  const renewingThumbnails = useRef(null)
  const failedThumbnails = useRef(new Set())
  const wasOpen = useRef(lockedSession.open)

  // Opening or sealing Locked changes what it lists and what the storage meter may say. Sealing
  // also takes whatever of it is on screen away with it.
  useEffect(() => {
    if (wasOpen.current === lockedSession.open) return

    wasOpen.current = lockedSession.open
    client.refetchQueries({include: [LOCKED, STORAGE]})

    if (!lockedSession.open && inLocked) {
      setPreviewId(null)
      setMoving(null)
      setRenaming(null)
      setSelection({place: null, keys: []})
    }
  }, [lockedSession.open])

  useEffect(() => () => {
    clearTimeout(searchAttempt.current)
    clearTimeout(searchSettle.current)
  }, [])

  // Loaded ahead of the click that opens them, since sign-in popups are only let through straight
  // after one. A failure here is met again, and told, on the click.
  useEffect(() => {
    if (!importEnabled) return

    prepareGooglePicker().catch(() => {})
    prepareOneDrivePicker().catch(() => {})
  }, [importEnabled])

  const preferences = userData?.currentUser.preferences
  const lockedHidden = Boolean(preferences?.lockedHidden)
  // Left out until the preference is known, so a hidden Locked never shows for a moment first.
  const lockedListed = lockedSession.open || Boolean(preferences && !lockedHidden)
  const lockedListing = lockedData?.locked
  const lockedOpen = lockedListing?.status === 'open'
  // Unlocked in this tab, with the listing still to say so: on its way, or about to be asked for,
  // since the ask follows the render that sees Locked open.
  const unlocking = lockedSession.open && !lockedOpen && (loadingLocked || wasOpen.current !== lockedSession.open)
  const lockedTrash = lockedSession.open && lockedOpen ? lockedListing.trash : {folders: [], uploads: []}

  const filesLoaded = {
    drive: driveListing.loaded || unknownFolder,
    recent: Boolean(recentData),
    starred: starredListing.loaded
  }[section] ?? true
  const loaded = Boolean(folderData && filesLoaded && (trashData || !inTrash) && (lockedData || !inLocked))
  const driveFolders = (folderData?.folders || []).filter(({name}) => name !== ROOT_FOLDER)
  // The place on screen is the drive's or Locked's, and so are the folders it is made from. Locked
  // hands over every file in it at once, so its places are made from those, as its folders' tallies.
  const folders = inLocked ? lockedListing?.folders || [] : driveFolders
  const lockedUploads = lockedListing?.uploads || []
  const foldersById = new Map(folders.map(entry => [entry.id, entry]))
  const folder = foldersById.get(folderId)
  const files = inLocked ?
    lockedUploads.filter(file => (folderId ? file.folderId === folderId : !foldersById.has(file.folderId))) :
    driveListing.files
  const lockedPictures = useLockedPictures({open: inLocked && lockedOpen, files})

  // Nothing follows a file resealing after a move the way the tray follows an upload, so the kept
  // listing would show it resealing until a reload. Opening the place it sits in asks again, once
  // its files are there to look at.
  const placeLoaded = inLocked ? Boolean(lockedData) : driveListing.loaded

  useEffect(() => {
    if (files.some(isResealing)) client.refetchQueries({include: [inLocked ? LOCKED : UPLOADS]})
  }, [section, folderId, placeLoaded])

  if (folderId && (inLocked ? lockedData : folderData) && !folder) return <Navigate to={inLocked ? '/locked' : '/'} replace />

  const childFolders = folders.filter(({parentId}) => (folderId ? parentId === folderId : !foldersById.has(parentId)))
  const needle = query.trim().toLowerCase()
  const matches = name => name.toLowerCase().includes(needle)
  const listedFolders = {
    drive: childFolders,
    locked: childFolders,
    trash: [...(trashData?.trash.folders || []), ...lockedTrash.folders]
  }[section] || []
  const listedFiles = {
    drive: files,
    locked: files,
    recent: recentData?.recentUploads || [],
    starred: starredListing.files,
    trash: [...(trashData?.trash.uploads || []), ...lockedTrash.uploads]
  }[section]
  const pagedListing = {drive: driveListing, starred: starredListing}[section]
  const shownFolders = listedFolders.filter(({name}) => matches(name))
  const shownFiles = listedFiles.filter(({filename}) => matches(filename)).map(lockedPictures.withPicture)
  const tallies = inLocked ? folderTallies(folders, lockedUploads, foldersById) : foldersById
  const previewing = listedFiles.find(({id}) => id === previewId)
  const view = chosenView ?? userData?.currentUser.preferences.driveView ?? 'list'

  const place = folderId || section
  const selectedKeys = new Set(selection.place === place ? selection.keys : [])
  const isSelected = entry => selectedKeys.has(selectionKey(entry))
  const selectedFolders = shownFolders.filter(({id}) => isSelected({kind: 'folder', id}))
  const selectedFiles = shownFiles.filter(({id}) => isSelected({kind: 'file', id}))
  const selectedEntries = [
    ...selectedFolders.map(({id, name}) => ({kind: 'folder', id, name})),
    ...selectedFiles.map(({id, filename}) => ({kind: 'file', id, name: filename}))
  ]
  const savable = selectedFiles.filter(isSecured)
  const busyFolders = busyFolderIds(folders, foldersById, tallies, transfers)

  // The listing is kept rather than asked for again on every visit, and a thumbnail's url runs out
  // ten minutes after signing at most, so a picture scrolled to or opened into late has lost it. One
  // ask brings fresh urls for all of them. Each url is asked about once, so a picture that is broken
  // rather than out of date can't keep the listing being asked for.
  const renewThumbnail = ({thumbnail: {url}}) => {
    if (failedThumbnails.current.has(url)) return

    failedThumbnails.current.add(url)
    renewingThumbnails.current ??= client.refetchQueries({include: UPLOAD_LISTINGS})
      .catch(() => null)
      .finally(() => { renewingThumbnails.current = null })
  }

  const uploadHere = fileList => upload(Array.from(fileList).map(file => ({file, folderId: folder?.id, locked: inLocked})))
  const uploadFolder = fileList => upload(Array.from(fileList).map(file => (
    {file, folderId: folder?.id, path: folderPath(file), locked: inLocked}
  )))

  // A picker only reports a change, so it is emptied after each pick or choosing the same files
  // twice would do nothing the second time.
  const importFrom = provider => {
    const {name, pick} = IMPORT_PICKERS[provider]
    // A pick that only signed in, or found signing in needed, carries on from the notice, whose
    // button is a fresh click for the next popup.
    const carryOn = (text, label) => setNotice({text, lasting: true, action: {label, onClick: () => importFrom(provider)}})

    return pick()
      .then(({signedIn, signInNeeded, ...picked}) => {
        if (signedIn) return carryOn(`Signed in to ${name}.`, 'Choose files')
        if (signInNeeded) return carryOn(`${name} asks you to sign in again.`, 'Sign in')
        if (!picked.fileIds.length) return

        setNotice({text: `Getting ${plural(picked.fileIds.length, 'file')} from ${name} ready to import…`, lasting: true})

        return imports.start({provider, ...picked, folderId: folder?.id ?? null})
          .then(() => setNotice(null))
          .catch(error => setNotice({text: refusalOf(error)}))
      })
      .catch(() => setNotice({text: `${name} couldn’t be opened. Try again.`}))
  }

  const addFromPost = fetchedMedia => {
    setImportingPost(false)
    uploadHere(fetchedMedia)
  }

  const continueImport = () => {
    setStartingImport(null)
    importFrom(startingImport)
  }

  const pickAgain = () => {
    const {provider} = imports.results

    imports.closeResults()
    importFrom(provider)
  }

  const handlePicked = send => event => {
    send(event.target.files)
    event.target.value = ''
  }

  const openNewFolder = () => setCreatingFolder(true)

  // The view has already changed on screen; a save that fails only means it isn't remembered next
  // time, which isn't worth interrupting for.
  const changeView = driveView => {
    setChosenView(driveView)
    updatePreferences({variables: {input: {driveView}}}).catch(() => {})
  }

  const download = file => {
    setNotice({text: `Preparing “${file.filename}” to download…`, lasting: true})
    save(file)
      .then(() => setNotice(null))
      .catch(() => setNotice({text: `“${file.filename}” couldn’t be downloaded. Try again.`}))
  }

  const toggleSelected = entry => {
    const key = selectionKey(entry)

    setSelection(current => {
      const keys = current.place === place ? current.keys : []

      return {place, keys: keys.includes(key) ? keys.filter(kept => kept !== key) : [...keys, key]}
    })
  }

  const clearSelection = () => setSelection({place, keys: []})

  const downloadSelected = () => {
    clearSelection()
    if (savable.length === 1) return download(savable[0])

    setNotice({text: `Preparing ${plural(savable.length, 'file')} to download…`, lasting: true})
    Promise.allSettled(savable.map(save)).then(results => {
      const failed = results.filter(({status}) => status === 'rejected').length

      setNotice(failed ? {text: `${failed} of ${plural(savable.length, 'file')} couldn’t be downloaded. Try again.`} : null)
    })
  }

  const dropFromSelection = keys => {
    const gone = new Set(keys)

    setSelection(current => ({...current, keys: current.keys.filter(key => !gone.has(key))}))
  }

  const restore = entries => {
    restoreItems({variables: {input: itemsInput(entries)}})
      .then(({data}) => {
        setNotice({text: restoredNotice(entries, countOf(data.restoreItems))})
        dropFromSelection(keysOf(data.restoreItems))
      })
      .catch(() => setNotice({text: `${itemsLabel(entries)} couldn’t be restored. Try again.`}))
  }

  const changeStar = (mutate, done, failed) => entry => {
    mutate({variables: {input: itemsInput([entry])}})
      .then(() => setNotice({text: `${itemsLabel([entry])} ${done}`}))
      .catch(() => setNotice({text: `${itemsLabel([entry])} ${failed}. Try again.`}))
  }

  const star = changeStar(starItems, 'added to Starred', 'couldn’t be starred')
  const unstar = changeStar(unstarItems, 'removed from Starred', 'couldn’t be removed from Starred')

  // The folder an item sits in, or null for the top of the drive.
  const sourceOf = ({kind, id}) => placeOf(
    kind === 'folder' ? foldersById.get(id)?.parentId : listedFiles.find(file => file.id === id)?.folderId,
    foldersById
  )

  const move = (entries, destination) => {
    setMoving(null)
    setDragging(null)
    moveItems({
      variables: {input: {...itemsInput(entries), destinationId: destination?.id ?? null, ...(inLocked && {locked: true})}},
      refetchQueries: inLocked ? [LOCKED] : [FOLDERS, ...UPLOAD_LISTINGS]
    })
      .then(({data}) => {
        setNotice({text: movedNotice(entries, countOf(data.moveItems), destination, inLocked ? LOCKED_NAME : TOP_NAME)})
        dropFromSelection(keysOf(data.moveItems))
      })
      .catch(() => setNotice({text: `${itemsLabel(entries)} couldn’t be moved. Try again.`}))
  }

  // Whatever needs Locked open waits for it: for the passphrase if it is sealed, or for one to be
  // set if there isn't one yet.
  const whenLockedOpen = (reason, then) => {
    if (lockedSession.open) return then()

    client.query({query: LOCKED, fetchPolicy: 'network-only'})
      .then(({data}) => setAsking({kind: data.locked.status === 'unset' ? 'passphrase' : 'unlock', reason, then}))
      .catch(() => setNotice({text: 'Locked couldn’t be reached. Try again.'}))
  }

  const sized = entries => entries.map(entry => (
    entry.kind === 'file' ? {...entry, filesize: listedFiles.find(({id}) => id === entry.id)?.filesize ?? null} : entry
  ))

  const askToSeal = entries => whenLockedOpen(
    'Unlock Locked to move items into it.',
    () => setCrossing({direction: 'in', entries: sized(entries)})
  )

  const askToUnseal = entries => setCrossing({direction: 'out', entries: sized(entries)})

  // Into Locked's top, or out to the top of the drive.
  const cross = ({direction, entries}) => {
    setCrossing(null)
    moveItems({
      variables: {input: {...itemsInput(entries), destinationId: null, locked: direction === 'in'}},
      refetchQueries: [FOLDERS, ...UPLOAD_LISTINGS, LOCKED, STORAGE]
    })
      .then(({data}) => {
        setNotice({text: crossedNotice(entries, countOf(data.moveItems), direction)})
        dropFromSelection(keysOf(data.moveItems))
      })
      .catch(() => setNotice({text: `${itemsLabel(entries)} couldn’t be moved. Try again.`}))
  }

  const lockNow = () => lockLocked().catch(() => {}).finally(sealLocked)

  // Only tried while Locked is hidden and sealed, and only once typing settles on something long
  // enough to be a passphrase. A wrong one is an ordinary search, with nothing to say it was tried.
  const searchFor = value => {
    setQuery(value)
    clearTimeout(searchSettle.current)
    searchSettle.current = setTimeout(() => setSearched(value.trim()), SEARCH_SETTLE)
    clearTimeout(searchAttempt.current)

    if (!lockedHidden || lockedSession.open || value.length < SHORTEST_PASSPHRASE) return

    searchAttempt.current = setTimeout(() => {
      tryPassphrase({variables: {input: {passphrase: value}}})
        .then(({data}) => {
          openLocked(data.unlockLocked)
          clearTimeout(searchSettle.current)
          setQuery('')
          setSearched('')
          navigate('/locked')
        })
        .catch(() => {})
    }, SEARCH_PAUSE)
  }

  const unlocked = () => {
    const then = asking?.then

    setAsking(null)
    then?.()
  }

  const settingsSaved = ({passphraseChanged}) => {
    setAsking(null)
    setNotice({text: passphraseChanged ? 'Locked settings saved, and the passphrase changed' : 'Locked settings saved'})
  }

  const dragItems = entry => {
    const entries = isSelected(entry) ? selectedEntries : [entry]

    setDragging(entries)

    return entries.length
  }

  // A folder can take anything dragged but itself. What is dragged comes from the folder on screen,
  // so it is never already in a folder listed there, nor in one above it in the trail.
  const dropInto = target => {
    if (!dragging || dragging.some(({kind, id}) => kind === 'folder' && id === target?.id)) return null

    return () => move(dragging, target)
  }

  const askToTrash = entries => {
    const uploading = entries.filter(({kind, id}) => kind === 'folder' && busyFolders.has(id))

    if (uploading.length) return setNotice({text: uploadingNotice(uploading)})

    setRemoving({mode: 'trash', entries})
  }

  const removed = ({data}) => {
    setNotice({text: DONE_NOTICES[removing.mode](data)})
    dropFromSelection(removing.entries.map(selectionKey))
    setRemoving(null)
  }

  const sealed = inLocked && lockedListing && !lockedOpen
  const opensInPlace = section === 'drive' || (inLocked && lockedOpen)
  const offersSeal = (['drive', 'recent', 'starred'].includes(section) && lockedListed) || (inLocked && lockedOpen)

  const driveItems = (
    <DriveItems
      folders={shownFolders}
      files={shownFiles}
      itemCount={id => tallies.get(id)?.itemCount || 0}
      folderSize={id => tallies.get(id)?.size || 0}
      view={view}
      query={needle}
      section={section}
      onOpenFile={file => setPreviewId(file.id)}
      onDownloadFile={download}
      onTrash={entry => askToTrash([entry])}
      onRestore={entry => restore([entry])}
      onPurge={entry => setRemoving({mode: 'purge', entries: [entry]})}
      onRename={setRenaming}
      onMove={entry => setMoving([entry])}
      onSeal={offersSeal ? entry => (inLocked ? askToUnseal : askToSeal)([entry]) : null}
      folderPath={inLocked ? '/locked/folders' : '/folders'}
      onThumbnailFailed={inLocked ? null : renewThumbnail}
      picturePending={inLocked ? lockedPictures.pending : null}
      onStar={star}
      onUnstar={unstar}
      selected={selectedKeys}
      onToggleSelect={toggleSelected}
      onDragItems={opensInPlace ? dragItems : null}
      onDragEnd={() => setDragging(null)}
      dropInto={dragging ? dropInto : null}
      onNearEnd={pagedListing?.hasMore ? pagedListing.loadMore : null}
    />
  )

  const listing = sealed ?
    (
      <LockedSeal
        status={lockedListing.status}
        items={lockedListing.sealedItems}
        unlocking={unlocking}
        onUnlock={() => setAsking({kind: 'unlock'})}
        onSetPassphrase={() => setAsking({kind: 'passphrase'})}
      />
    ) :
    driveItems

  const selectionActions = inTrash ?
    {
      onRestore: () => restore(selectedEntries),
      onPurge: () => setRemoving({mode: 'purge', entries: selectedEntries})
    } :
    {
      onDownload: savable.length ? downloadSelected : null,
      onMove: () => setMoving(selectedEntries),
      onSeal: offersSeal ? () => (inLocked ? askToUnseal : askToSeal)(selectedEntries) : null,
      sealsOut: inLocked,
      onTrash: () => askToTrash(selectedEntries)
    }

  const selectionBar = selectedEntries.length ?
    {count: selectedEntries.length, onClear: clearSelection, ...selectionActions} :
    null

  // The drive gives way to the sign-in card as soon as the tokens go, so nothing here waits on the
  // revocation; what this person's drive left in the cache goes with them.
  // Locked is sealed first, while the session to say so with is still there.
  const signOut = () => (lockedSession.open ? lockLocked().catch(() => {}) : Promise.resolve())
    .then(() => {
      sealLocked()

      return endSession()
    })
    .then(() => client.clearStore())

  const openState = lockedHidden ? 'hiding' : 'open'
  const lockedState = lockedSession.open ? openState : 'sealed'

  const lockedControls = inLocked && lockedOpen ?
    {
      remaining: lockedSession.remaining,
      hidden: lockedHidden,
      onSettings: () => setAsking({kind: 'settings'}),
      onLockNow: lockNow
    } :
    null

  return (
    <Loader loading={(loadingFolders || driveListing.loading || starredListing.loading || loadingRecent || loadingTrash || loadingLocked) && !loaded}>
      <div className={classnames('my-drive', {'in-locked': inLocked})}>
        <DriveRail
          section={section}
          storage={storageData?.storage}
          locked={lockedListed ? lockedState : null}
          onNewFolder={openNewFolder}
          onUploadFiles={() => filePicker.current.click()}
          onUploadFolder={() => folderPicker.current.click()}
          onImport={importEnabled && section === 'drive' ? setStartingImport : null}
          importing={imports.starting ?? (imports.active ? imports.provider : null)}
        />
        <main className='drive-main'>
          <DriveTopbar
            trail={folder ? trailTo(folder, foldersById) : []}
            section={section}
            query={query}
            onQueryChange={searchFor}
            view={view}
            onViewChange={changeView}
            user={userData?.currentUser}
            onSignOut={signOut}
            onDropInto={dragging ? target => dropInto(target)?.() : null}
          />
          <DriveToolbar
            count={sealed ? lockedListing.sealedItems.length : shownFolders.length + shownFiles.length}
            more={!sealed && Boolean(pagedListing?.hasMore)}
            onNewFolder={opensInPlace ? openNewFolder : null}
            selection={selectionBar}
            locked={lockedControls}
          />
          <DropZone onDrop={opensInPlace ? uploadHere : null}>{listing}</DropZone>
        </main>
        <UploadTray
          uploads={[...imports.entries, ...transfers]}
          onDismiss={() => {
            dismissUploads()
            imports.dismiss()
          }}
        />
      </div>

      <input ref={filePicker} type='file' multiple hidden onChange={handlePicked(uploadHere)} />
      <input ref={folderPicker} type='file' multiple hidden webkitdirectory='' onChange={handlePicked(uploadFolder)} />

      {startingImport && (
        <ImportStartDialog
          provider={startingImport}
          destination={folder?.name ?? TOP_NAME}
          onClose={() => setStartingImport(null)}
          onContinue={continueImport}
        />
      )}

      {importingPost && (
        <XPostDialog destination={folder?.name ?? TOP_NAME} onClose={() => setImportingPost(false)} onAdd={addFromPost} />
      )}

      {imports.results && (
        <ImportResultsDialog {...imports.results} onClose={imports.closeResults} onPickAgain={pickAgain} />
      )}

      {creatingFolder && <NewFolderDialog parent={folder} locked={inLocked} onClose={() => setCreatingFolder(false)} />}

      {renaming && <RenameDialog entry={renaming} locked={inLocked} onClose={() => setRenaming(null)} />}

      {removing && <TrashDialog {...removing} onClose={() => setRemoving(null)} onDone={removed} />}

      {moving && (
        <MoveDialog
          entries={moving}
          folders={folders}
          sources={moving.map(sourceOf)}
          start={startOf(moving.map(sourceOf))}
          topName={inLocked ? LOCKED_NAME : TOP_NAME}
          onClose={() => setMoving(null)}
          onMove={destination => move(moving, destination)}
        />
      )}

      {previewing && (
        <FilePreview
          file={previewing}
          siblings={shownFiles.filter(isSecured)}
          downloads={downloads}
          sealed={inLocked}
          onOpen={file => setPreviewId(file.id)}
          onClose={() => setPreviewId(null)}
          onDownload={download}
          onSeal={offersSeal ? file => (inLocked ? askToUnseal : askToSeal)([{kind: 'file', id: file.id, name: file.filename}]) : null}
          onNotice={text => setNotice({text})}
        />
      )}

      {crossing && <LockedMoveDialog {...crossing} onClose={() => setCrossing(null)} onConfirm={() => cross(crossing)} />}

      {asking?.kind === 'unlock' && (
        <UnlockDialog
          resealAfter={preferences?.lockedResealAfter ?? 300}
          reason={asking.reason}
          onClose={() => setAsking(null)}
          onUnlocked={unlocked}
          onForgot={() => setAsking({kind: 'reset'})}
        />
      )}

      {asking?.kind === 'passphrase' && <PassphraseDialog onClose={() => setAsking(null)} onUnlocked={unlocked} />}

      {asking?.kind === 'settings' && preferences && (
        <LockedSettingsDialog preferences={preferences} onClose={() => setAsking(null)} onSaved={settingsSaved} />
      )}

      {asking?.kind === 'reset' && (
        <ResetLockedDialog
          itemCount={lockedListing?.sealedItems.length}
          onClose={() => setAsking(null)}
          onReset={() => {
            setAsking(null)
            setNotice({text: 'Locked was reset. Set a new passphrase the next time you move something in.'})
          }}
        />
      )}

      <Snackbar
        open={Boolean(notice)}
        message={notice?.text}
        action={notice?.action && (
          <BaseButton
            className='notice-action'
            variant='text'
            onClick={() => {
              setNotice(null)
              notice.action.onClick()
            }}>
            {notice.action.label}
          </BaseButton>
        )}
        autoHideDuration={notice?.lasting ? null : NOTICE_DURATION}
        onClose={(event, reason) => reason !== 'clickaway' && setNotice(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        slotProps={{content: {className: 'drive-notice'}}}
      />
    </Loader>
  )
}

MyDrive.propTypes = {
  section: PropTypes.oneOf(SECTIONS)
}

export default MyDrive
